import React, { useState } from 'react';
import { useUpdateEffect, useLifecycles } from 'react-use';
import loadJs from 'load-js';
import {
  Alert,
  Box,
  Button,
  Grid,
  InputAdornment,
  Pagination,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InfoIcon from '@mui/icons-material/Info';
import { styled } from '@mui/styles';

import { getXY, Log } from '../../common/util';

const AddressTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '& input': {
      color: theme.palette.secondary.dark,
      WebkitTextFillColor: theme.palette.secondary.dark,
    },
    '& fieldset': {
      borderColor: theme.palette.secondary.main,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.secondary.dark,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.secondary.dark,
    },
    '&.Mui-disabled': {
      color: theme.palette.secondary.dark,
      '& fieldset': {
        borderColor: theme.palette.secondary.dark,
      },
    },
  },
}));

let navermap;
const clientId = '2hqmcff0c3';
const COUNT = 10;

const SubUserGeoInputForm = (props) => {
  const { onChange, value: v } = props;
  const [loading, setLoading] = useState(false);
  const [address, setAddress] = useState('');
  const [search, setSearch] = useState('');
  const [searchResults, setResults] = useState(null);
  const [pageInfo, setPageInfo] = useState({
    pages: [1],
    totalCount: 0,
    current: 0,
  });
  const [error, setError] = useState('');
  let loadScriptPromise = null;
  let isSubscribed = true;

  const loadNavermapsScript = () => {
    if (loadScriptPromise) {
      return loadScriptPromise;
    }

    loadScriptPromise = loadJs(
      `https://oapi.map.naver.com/openapi/v3/maps.js?ncpClientId=${clientId}&submodules=geocoder`,
    ).then(() => {
      const navermaps = window.naver.maps;

      if (navermaps.jsContentLoaded) {
        return navermaps;
      }

      const loadingJsContent = new Promise((resolve) => {
        navermaps.onJSContentLoaded = () => {
          resolve(navermaps);
        };
      });

      return loadingJsContent;
    });

    return loadScriptPromise;
  };

  const onError = (e) => {
    setLoading(false);
    Log(e);
    if ('message' in e) {
      setError(e.message);
    }
    // Alert('위치 설정 권한을 허용해주세요.');
  };

  const getAddress = (coords) => {
    if (!isSubscribed) return;

    navermap.Service.reverseGeocode(
      {
        coords,
      },
      (status, response) => {
        if (status !== navermap.Service.Status.OK) {
          return onError({ messeage: '주소 검색에 실패 했습니다.' });
        }
        setError(false);
        const result = response.v2; // 검색 결과의 컨테이너
        const items = result.results; // 검색 결과의 배열
        let formattedAddress;
        const valueObj = {
          ...getXY(coords.y, coords.x),
          lat: parseFloat(coords.y),
          lng: parseFloat(coords.x),
        };
        items.forEach(({ name, region }) => {
          if (name === 'legalcode') {
            const addressArr = Object.keys(region).reduce((prev, curr, idx) => {
              if (idx > 0 && region[curr].name) {
                prev.push(region[curr].name);
              }
              return prev;
            }, []);
            formattedAddress = addressArr.join(' ');
            valueObj.address = addressArr;
          }
        });
        setLoading(false);
        setAddress(formattedAddress || '');
        if (!formattedAddress) {
          return onError({ messeage: '주소가 유효하지 않습니다.' });
        }
        setSearch('');
        setResults(null);

        return onChange(valueObj);
      },
    );
  };

  const handleOnChange = (e) => {
    setSearch(e.target.value);
  };

  const getGeocode = (q, p) => {
    try {
      navermap.Service.geocode(
        {
          query: q,
          page: p,
          count: COUNT,
        },
        (status, response) => {
          if (status !== navermap.Service.Status.OK) {
            return onError({ messeage: '주소 검색에 실패 했습니다.' });
          }

          const result = response.v2; // 검색 결과의 컨테이너
          const { addresses, meta } = result; // 검색 결과의 배열
          const { totalCount, page } = meta;
          setPageInfo({ ...pageInfo, totalCount, current: page });
          return setResults(addresses);
        },
      );
    } catch (e) {
      onError(e);
    }
  };

  // const handleSaveClick = () => value && onSave && onSave(value);

  useUpdateEffect(() => {
    if (search.length < 1 || !RegExp(/[가-힣0-9\s]/, 'gi').test(search)) return;
    getGeocode(search, 1);
  }, [search]);

  useUpdateEffect(() => {
    const { totalCount } = pageInfo;
    const add = totalCount % COUNT > 0 ? 1 : 0;
    const length = parseInt(totalCount / COUNT, 10) + add;
    const newPages = Array.from({ length }, (j, i) => i + 1);
    setPageInfo({ ...pageInfo, pages: newPages });
  }, [pageInfo.totalCount]);

  useLifecycles(
    () => {
      setLoading(true);
      if (isSubscribed && !loading) {
        loadNavermapsScript()
          .then(() => {
            navermap = window.naver && window.naver.maps;
            const { lat, lng } = v;
            return getAddress(new navermap.LatLng(lat, lng));
          })
          .catch(onError);
      }
    },
    () => {
      isSubscribed = false;
    },
  );
  return (
    <Grid container item xs={12}>
      <Typography variant="subtitle2">위치설정</Typography>
      <AddressTextField
        value={address}
        size="small"
        margin="dense"
        disabled
        fullWidth
      />
      <TextField
        placeholder="도로명 또는 동을 기준으로 입력하세요."
        onChange={handleOnChange}
        value={search}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        size="small"
        margin="dense"
        fullWidth
      />
      {error && <Alert color="error">{error}</Alert>}
      <Box
        sx={{
          maxHeight: '140px',
          overflowY: 'auto',
          width: '100%',
          margin: 1,
        }}
      >
        {searchResults &&
          searchResults.map(({ roadAddress, x, y }) => (
            <Button
              variant="text"
              key={x + y}
              onClick={() => getAddress({ x, y })}
            >
              {roadAddress}
            </Button>
          ))}
        {searchResults && searchResults.length > 0 && (
          <Pagination
            count={pageInfo.pages.length}
            size="small"
            onChange={(e, page) => getGeocode(search, page)}
            sx={{ ul: { justifyContent: 'end' } }}
          />
        )}
      </Box>
      <InfoIcon color="secondary" sx={{ mr: 1 }} />
      <Typography variant="body2">
        위치 설정은 상세주소를 선택해도 동(읍/면/리)단위로 변환되어 설정됩니다.
      </Typography>
    </Grid>
  );
};

export default SubUserGeoInputForm;
